/**
 * The live access token: what every API request carries.
 *
 * The realm user sits in localStorage, so every tab shares one sign-in
 * (see `oidc.ts`). A request asks for its token here: a live one is used
 * as stored, an expired one is renewed with the refresh token first. A
 * laptop waking up, a reload after a deploy, or a tab opened later then
 * carries on without a trip through the realm.
 *
 * One renewal runs at a time in a tab, and across tabs under a Web Lock,
 * so tabs holding the same refresh token never spend it twice. Only the
 * realm refusing the refresh token ends the session; a failure on the way
 * there keeps the current token until it expires.
 */
import { ErrorResponse, type User } from 'oidc-client-ts';

import { getUserManager } from './oidc.js';

/** Renew a token this many seconds before it expires, as oidc-client-ts's own timer would. */
export const RENEW_BEFORE_S = 60;
const RENEW_LOCK = 'optum-admin:session-renewal';

let inflight: Promise<User | null> | null = null;

/** A stored user whose access token can still be sent. */
function isLive(user: User | null): user is User {
  return Boolean(user?.access_token) && user?.expired === false;
}

/** A stored user with more than the renewal margin left: nothing to renew yet. */
function isFresh(user: User | null): boolean {
  return isLive(user) && (user.expires_in ?? 0) > RENEW_BEFORE_S;
}

/** Runs `fn` under the cross-tab renewal lock where the browser has Web Locks. */
async function withLock(fn: () => Promise<User | null>): Promise<User | null> {
  // Absent outside a secure context, and null in some test environments.
  const locks: LockManager | null | undefined = navigator.locks;
  if (!locks) return fn();
  return await locks.request(RENEW_LOCK, fn);
}

/** One renewal attempt, read against the store as it is once the lock is held. */
async function renewUnderLock(): Promise<User | null> {
  const manager = getUserManager();
  if (!manager) return null;
  // Another tab may have renewed while this one waited for the lock.
  const stored = await manager.getUser();
  if (isFresh(stored)) return stored;
  if (!stored?.refresh_token) return isLive(stored) ? stored : null;
  try {
    return await manager.signinSilent();
  } catch (err) {
    if (err instanceof ErrorResponse) {
      // The realm refused the refresh token: the session is over, in every tab.
      await manager.removeUser();
      return null;
    }
    console.warn('Session renewal failed; the current token stays until it expires.', err);
    return isLive(stored) ? stored : null;
  }
}

/** Renews the session once; callers arriving meanwhile share that renewal. */
export function renewSession(): Promise<User | null> {
  inflight ??= withLock(renewUnderLock).finally(() => {
    inflight = null;
  });
  return inflight;
}

/** The token the next request carries: the stored one while live, else a renewed one, else none. */
export async function liveToken(): Promise<string | null> {
  const manager = getUserManager();
  if (!manager) return null;
  const stored = await manager.getUser();
  if (isLive(stored)) return stored.access_token;
  if (!stored) return null;
  const renewed = await renewSession();
  return renewed?.access_token ?? null;
}
